import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, money } from "@/lib/api";
import { Loader, StatusBadge, Money } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, Minus, Plus, ArrowLeft, Package } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/marketplace/products/${id}`).then((r) => { setP(r.data); setQty(r.data.moq || 1); }).catch(() => setP(false));
  }, [id]);

  if (p === null) return <Loader />;
  if (!p) return <div className="py-10 text-center text-ink/60">Product not found.</div>;

  async function addToCart(buyNow) {
    setBusy(true);
    try {
      await api.post("/cart/add", { product_id: p.id, quantity: qty });
      toast.success("Added to cart");
      nav(buyNow ? "/retailer/cart" : "/retailer/marketplace");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  }

  const dec = () => setQty((q) => Math.max(p.moq || 1, q - (p.units_per_case || 1)));
  const inc = () => setQty((q) => q + (p.units_per_case || 1));

  return (
    <div className="space-y-5">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-ink/50 hover:text-forest"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <div className="aspect-square bg-psage/40">
            {p.images?.[0] ? <img src={p.images[0]} alt={p.name} className="h-full w-full object-cover" />
              : <div className="flex h-full items-center justify-center"><Package className="h-16 w-16 text-msage" /></div>}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2">
            <span className="overline text-olive">{p.brand}</span>
            {p.manufacturer?.verification_status === "VERIFIED" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-psage px-2 py-0.5 text-xs font-semibold text-forest">
                <ShieldCheck className="h-3 w-3" /> Verified
              </span>
            )}
          </div>
          <h1 className="mt-1 font-heading text-2xl font-semibold text-ink">{p.name}</h1>
          <p className="mt-1 text-sm text-ink/60">by {p.manufacturer_name} · {p.manufacturer?.state}</p>

          <div className="mt-5 grid grid-cols-2 gap-3 rounded-xl border border-border bg-card p-4">
            <Stat label="MRP" value={money(p.mrp)} strike />
            <Stat label="VIPER OWL Price" value={money(p.retailer_price)} accent />
            <Stat label="Potential Margin" value={money(p.margin)} accent />
            <Stat label="Margin %" value={`${p.margin_pct}%`} accent />
            <Stat label="MOQ" value={`${p.moq} units`} />
            <Stat label="Units / Case" value={p.units_per_case} />
            <Stat label="GST" value={`${p.gst_rate}%`} />
            <Stat label="Dispatch" value={p.dispatch_time || "2-4 days"} />
          </div>

          {p.description && <p className="mt-4 text-sm text-ink/70 leading-relaxed">{p.description}</p>}

          <div className="mt-5 flex items-center gap-3">
            <div className="flex items-center rounded-md border border-border">
              <Button data-testid="qty-dec" variant="ghost" size="icon" onClick={dec}><Minus className="h-4 w-4" /></Button>
              <Input data-testid="qty-input" className="w-16 border-0 text-center num" value={qty}
                onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} />
              <Button data-testid="qty-inc" variant="ghost" size="icon" onClick={inc}><Plus className="h-4 w-4" /></Button>
            </div>
            <span className="text-sm text-ink/60">{p.inventory} in stock</span>
          </div>
          <p className="mt-1 text-xs text-ink/50">Order value: <span className="num text-forest">{money(p.retailer_price * qty)}</span> + GST</p>

          <div className="mt-4 flex gap-3">
            <Button data-testid="add-to-cart" variant="outline" className="flex-1 border-forest text-forest hover:bg-psage" disabled={busy} onClick={() => addToCart(false)}>Add to Cart</Button>
            <Button data-testid="buy-now" className="flex-1 bg-forest text-white hover:bg-forest/90" disabled={busy} onClick={() => addToCart(true)}>Buy Now</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, strike }) {
  return (
    <div>
      <div className="overline text-olive">{label}</div>
      <div className={`num text-lg font-semibold ${accent ? "text-forest" : strike ? "text-msage line-through" : "text-ink"}`}>{value}</div>
    </div>
  );
}
